import React, { useCallback, useState } from "react";
import clsx from "clsx";

import { CloseButton } from "components";

import { ReactComponent as CheckIcon } from "static/images/checked.svg";
import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import { ReactComponent as NoticeIcon } from "static/images/warning-icon.svg";
import styles from "./alert.module.scss";

// prettier-ignore
export type AlertVariant = "primaryDark" | "success" | "warning" | "info" | "failure" | "notice" | "default" | "default-orange";

const getIcon = (variant: AlertVariant) => {
	switch (variant) {
		case "success":
			return <CheckIcon className="mr-3" width="22" />;
		case "warning":
		case "info":
		case "failure":
		case "notice":
			return <NoticeIcon className="mr-3" width="22" />;
		case "default-orange":
		case "default":
			return <WarningIcon className="mr-3 text-warning" width="22" />;
		default:
			return undefined;
	}
};

type AlertProps = {
	message: any;
	variant: AlertVariant;
	icon?: any;
	onClose?: Function;
	centered?: boolean;
	className?: string;
	style?: React.CSSProperties;
	name?: string;
	isShowCloseButton?: boolean;
	alignCloseButton?: "none" | "top" | "center";
};

const Alert = ({
	message,
	variant,
	icon,
	onClose,
	centered,
	className,
	style,
	/** Required if you want to save alert visibility state to session storage */
	name,
	isShowCloseButton = false,
	alignCloseButton = "top",
}: AlertProps) => {
	const nameInStorage = `alertMsgHidden_${name}`;

	const [isHiddenComponent, setIsHiddenComponent] = useState<boolean>(
		sessionStorage.getItem(nameInStorage) === "true",
	);

	const handleClose = useCallback(() => {
		onClose?.();

		if (name) {
			setIsHiddenComponent(true);
			sessionStorage.setItem(nameInStorage, "true");
		}
	}, [nameInStorage, name, onClose]);

	if (isHiddenComponent) {
		return null;
	}

	return (
		<div
			className={clsx(
				className,
				styles.alert,
				styles[variant],
				centered && styles.centered,
			)}
			style={style}
		>
			<div className={styles.icon}>{icon || getIcon(variant)}</div>
			<div className={styles.message}>{message}</div>
			{(onClose || isShowCloseButton) && (
				<CloseButton
					onClick={handleClose}
					className={clsx(styles.closeBtn, styles[alignCloseButton])}
				/>
			)}
		</div>
	);
};

export default Alert;
